import { Badge } from '@paulownia/shared/components/ui/badge';
import { Button } from '@paulownia/shared/components/ui/button';
import { type OrderLine, type Product, getOptionLabels, getLinePrice, isVariablePriceLine, isLineWeighed, formatQuantityDisplay, getLineCompositionSelections, getCompositionOption, safeToFixed } from '@paulownia/shared/types';
import { Scale } from 'lucide-react';

interface OrderLineCardProps {
  line: OrderLine;
  product: Product | undefined;
  /** All products for composition label resolution */
  allProducts?: Product[];
  /** Show checkbox for prepared status */
  showCheckbox?: boolean;
  /** Is this line fully prepared */
  isPrepared?: boolean;
  /** Callback when checkbox is clicked */
  onTogglePrepared?: () => void;
  /** Show weigh-in button for variable price lines */
  showWeighIn?: boolean;
  /** Callback when weigh-in button is clicked */
  onWeighIn?: () => void;
}

export function OrderLineCard({
  line,
  product,
  allProducts = [],
  showCheckbox = false,
  isPrepared = false,
  onTogglePrepared,
  showWeighIn = false,
  onWeighIn,
}: OrderLineCardProps) {
  // Calculate price using new getLinePrice helper (handles finalPrice > estimatedPrice > calculated)
  const lineTotal = getLinePrice(line, product);

  // Check if this is a variable price line (needs weighing)
  const isVariable = isVariablePriceLine(line);
  const isWeighed = isLineWeighed(line);

  // Format quantity display using centralized function
  const quantityDisplay = formatQuantityDisplay(line.quantity, product, line.portionSizeId, line);

  // Get option labels
  const optionLabels = product ? getOptionLabels(line, product) : [];

  // Get composition selections if this product has a composition option
  const compositionOption = product ? getCompositionOption(product) : undefined;
  const compositionSelections = product ? getLineCompositionSelections(line, product) : [];
  const hasComposition = compositionOption && compositionSelections.length > 0;

  return (
    <div
      className={`rounded-lg border px-3 py-2 transition-colors ${
        isPrepared
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start gap-2">
        {/* Checkbox */}
        {showCheckbox && onTogglePrepared && (
          <button
            onClick={onTogglePrepared}
            className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all ${
              isPrepared
                ? 'bg-green-600 border-green-600 text-white'
                : 'border-gray-300 hover:border-green-500'
            }`}
            title={isPrepared ? 'Annuler préparation' : 'Marquer préparé'}
          >
            {isPrepared && <span className="text-xs">✓</span>}
          </button>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Product name on dedicated line */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-900 font-medium">
              {product?.name || 'Produit inconnu'}
            </span>
            <div className="flex items-center gap-2">
              {/* Weigh-in button for variable price lines */}
              {showWeighIn && isVariable && onWeighIn && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onWeighIn();
                  }}
                  className={`h-7 px-2 ${isWeighed ? 'border-green-300 text-green-700' : 'border-amber-300 text-amber-700'}`}
                >
                  <Scale className="h-3.5 w-3.5 mr-1" />
                  {isWeighed ? 'Modifier' : 'Peser'}
                </Button>
              )}
              {/* Price - show ≈ for estimated prices, checkmark for weighed */}
              {lineTotal !== null && Number.isFinite(lineTotal) && (
                <span className={`font-semibold whitespace-nowrap flex-shrink-0 ${isWeighed ? 'text-green-700' : isVariable ? 'text-amber-600' : 'text-green-700'}`}>
                  {isVariable && !isWeighed && '≈ '}
                  {safeToFixed(lineTotal, 2)}€
                  {isWeighed && ' ✓'}
                </span>
              )}
            </div>
          </div>

          {/* Quantity and options on second line */}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">
              {quantityDisplay}
            </span>
            {/* Show servings badge for WEIGHT_ESTIMATED */}
            {line.servings && (
              <Badge
                variant="outline"
                className="text-xs bg-blue-50 text-blue-700 border-blue-300 px-1.5 py-0"
              >
                {line.servings} pers
              </Badge>
            )}
            {optionLabels.map((label, optIdx) => (
              <Badge
                key={optIdx}
                variant="outline"
                className="text-xs bg-amber-50 text-amber-700 border-amber-300 px-1.5 py-0"
              >
                {label}
              </Badge>
            ))}
          </div>

          {/* Composition details */}
          {hasComposition && (
            <div className="mt-2 text-xs text-gray-600 bg-purple-50 border border-purple-200 rounded p-2">
              <div className="font-medium text-purple-700 mb-1">Composition:</div>
              <div className="space-y-0.5">
                {compositionSelections.filter(s => s.quantity > 0).map(selection => {
                  // Try to find the product name from allProducts, fallback to component label
                  const componentProduct = allProducts.find(p => p.id === selection.productId);
                  const component = compositionOption?.components?.find(c => c.productId === selection.productId);
                  const label = componentProduct?.shortenName || componentProduct?.name || component?.label || 'Inconnu';
                  return (
                    <div key={selection.productId} className="flex items-center gap-1">
                      <span className="font-medium">{selection.quantity}×</span>
                      <span>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
